import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getRepo, isEmail, type Article } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { ArticleCard } from '../../components/ArticleCard';
import { Seo } from '../../components/Seo';
import { Button, Card, EmptyState, Field, Input, Spinner, useToast } from '../../components/ui';

function NewsletterCard() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!isEmail(email)) { setError('Enter a valid email address.'); return; }
    setBusy(true);
    const res = await getRepo().subscribe(email);
    setBusy(false);
    if (!res.ok) { setError(res.error ?? 'Something went wrong.'); return; }
    setEmail('');
    toast('You are subscribed. Check your inbox to confirm.');
  }

  return (
    <Card className="bg-accent text-white">
      <div className="p-5">
        <p className="font-display text-lg">Stay informed</p>
        <p className="mt-1 text-sm text-white/70">Get the week’s best writing in your inbox.</p>
        <form className="mt-4 space-y-2" onSubmit={submit} noValidate>
          <Field label="" error={error}>
            <Input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" aria-label="Email address"
              className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
            />
          </Field>
          <Button type="submit" variant="primary" className="w-full" disabled={busy}>
            {busy ? 'Subscribing…' : 'Subscribe'}
          </Button>
        </form>
      </div>
    </Card>
  );
}

export function HomePage() {
  const { data, loading, error } = useAsync<Article[]>(() => getRepo().listPublicArticles({ limit: 60 }), []);
  const articles = data ?? [];
  const [lead, ...rest] = articles;
  const trending = [...articles].sort((a, b) => b.viewCount - a.viewCount).slice(0, 5);
  const latestNews = articles.slice(0, 5);

  return (
    <>
      <Seo name="home" />
      {loading ? <Spinner label="Loading the front page…" /> : null}
      {error ? <div className="mx-auto max-w-6xl px-4 py-10 text-danger">{error}</div> : null}

      {articles.length > 0 ? (
        <div className="border-b border-border bg-primary/5">
          <div className="mx-auto flex max-w-6xl items-center gap-4 overflow-hidden px-4 py-2 text-xs">
            <span className="shrink-0 font-semibold uppercase tracking-widest text-primary">Latest</span>
            <ul className="flex gap-6 overflow-hidden whitespace-nowrap">
              {latestNews.map((a) => (
                <li key={a.id} className="truncate">
                  <Link to={`/article/${a.slug}`} className="text-foreground/80 hover:text-primary">{a.title}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="mx-auto max-w-6xl px-4 py-10">
        <p className="mb-8 text-center font-display text-lg italic text-muted">
          Essays, reviews, interviews and art from the people making culture.
        </p>

        {lead ? <ArticleCard article={lead} variant="lead" /> : null}

        <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_300px]">
          <section aria-labelledby="wall">
            <h2 id="wall" className="mb-6 border-b border-border pb-2 font-display text-xl font-semibold tracking-wide">The Wall</h2>
            {rest.length ? (
              <div className="grid gap-8 sm:grid-cols-2">
                {rest.slice(0, 8).map((a) => <ArticleCard key={a.id} article={a} />)}
              </div>
            ) : (
              <EmptyState title="Nothing published yet" description="Once articles are published they will appear here." />
            )}
          </section>

          <aside className="space-y-8">
            <section aria-labelledby="trending">
              <h2 id="trending" className="mb-3 border-b border-border pb-2 font-display text-lg font-semibold">Trending now</h2>
              <ol className="space-y-1">
                {trending.map((a, i) => (
                  <li key={a.id} className="flex gap-3 py-1">
                    <span className="font-display text-xl text-primary/60">{String(i + 1).padStart(2, '0')}</span>
                    <Link to={`/article/${a.slug}`} className="text-sm leading-snug hover:text-primary">{a.title}</Link>
                  </li>
                ))}
              </ol>
            </section>
            <section aria-labelledby="latestnews">
              <h2 id="latestnews" className="mb-3 border-b border-border pb-2 font-display text-lg font-semibold">Latest news</h2>
              <ul className="divide-y divide-border">
                {latestNews.map((a) => <li key={a.id}><ArticleCard article={a} variant="compact" /></li>)}
              </ul>
            </section>
            <NewsletterCard />
          </aside>
        </div>
      </div>
    </>
  );
}
